import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";
import { logger } from "@/lib/logger";

const CartContext = createContext(null);
const STORAGE_KEY = "cmp_cart_v1";

// Cart lines are non-sensitive (product ids, variant skus, quantities). localStorage
// is intentional here: it survives page reloads without a backend round-trip and
// contains nothing that would harm the user if read by malicious script. Auth tokens
// are NEVER stored client-side — they live in an httpOnly cookie set by the backend.

const readStorage = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
  } catch (err) {
    logger.warn("Cart storage read failed; starting empty", err);
    return [];
  }
};

const writeStorage = (items) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch (err) {
    // Quota exceeded or storage disabled — log but do not surface to the UI.
    logger.warn("Cart storage write failed", err);
  }
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(readStorage);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    writeStorage(items);
  }, [items]);

  const addItem = useCallback((line) => {
    setItems((prev) => {
      const key = `${line.product_id}::${line.variant_sku}`;
      const idx = prev.findIndex((p) => `${p.product_id}::${p.variant_sku}` === key);
      if (idx >= 0) {
        const clone = [...prev];
        clone[idx] = { ...clone[idx], quantity: clone[idx].quantity + line.quantity };
        return clone;
      }
      return [...prev, line];
    });
    setOpen(true);
  }, [setItems, setOpen]);

  const updateQty = useCallback((key, qty) => {
    setItems((prev) =>
      prev
        .map((p) => (`${p.product_id}::${p.variant_sku}` === key ? { ...p, quantity: qty } : p))
        .filter((p) => p.quantity > 0)
    );
  }, [setItems]);

  const removeItem = useCallback((key) => {
    setItems((prev) => prev.filter((p) => `${p.product_id}::${p.variant_sku}` !== key));
  }, [setItems]);

  const clear = useCallback(() => setItems([]), [setItems]);

  const subtotal = useMemo(
    () => items.reduce((s, i) => s + i.unit_price * i.quantity, 0),
    [items]
  );
  const count = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);

  const value = useMemo(
    () => ({ items, open, setOpen, addItem, updateQty, removeItem, clear, subtotal, count }),
    [items, open, setOpen, addItem, updateQty, removeItem, clear, subtotal, count]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
